import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { IItem } from '../Items/IItem'
import type { IList } from '../List/IList'

export enum HistoryActionType {
  ITEM_UPDATE = 'ITEM_UPDATE',
  ITEM_DELETE = 'ITEM_DELETE',
  ITEMS_DELETE = 'ITEMS_DELETE',
  LIST_DELETE = 'LIST_DELETE',
}

type HistoryItem = {
  id?: string
  type: HistoryActionType
  listId?: string
  itemId?: string
  itemsById?: { [key: string]: IItem }
  itemIds?: string[]
  listsById?: { [key: string]: IList }
  listIds?: string[]
  timestamp: number
}

type HistoryState = {
  undoStack: HistoryItem[]
  redoStack: HistoryItem[]
  maxStackSize: number
  nextActionId: number
}

const initialState: HistoryState = {
  undoStack: [],
  redoStack: [],
  maxStackSize: 50,
  nextActionId: 1,
}

const historySlice = createSlice({
  name: 'history',
  initialState,
  reducers: {
    pushToHistory: (
      state,
      action: PayloadAction<Omit<HistoryItem, 'timestamp' | 'id'>>
    ) => {
      // Clear redo stack when a new action is performed
      state.redoStack = []

      // Add action to undo stack
      state.undoStack.push({
        ...action.payload,
        id: `action_${state.nextActionId++}`,
        timestamp: Date.now(),
      })

      // Maintain max stack size
      if (state.undoStack.length > state.maxStackSize) {
        state.undoStack.shift()
      }
    },

    // Move action from undo to redo stack
    undo: (state) => {
      if (state.undoStack.length > 0) {
        const action = state.undoStack.pop()
        if (action) {
          state.redoStack.push(action)

          // Maintain max stack size
          if (state.redoStack.length > state.maxStackSize) {
            state.redoStack.shift()
          }
        }
      }
    },

    // Move action from redo to undo stack
    redo: (state) => {
      if (state.redoStack.length > 0) {
        const action = state.redoStack.pop()
        if (action) {
          state.undoStack.push(action)

          // Maintain max stack size
          if (state.undoStack.length > state.maxStackSize) {
            state.undoStack.shift()
          }
        }
      }
    },

    clearHistory: (state) => {
      state.undoStack = []
      state.redoStack = []
    },
  },
})

export const { pushToHistory, undo, redo, clearHistory } = historySlice.actions
export default historySlice.reducer
